
import React, { memo } from 'react';
import { formatText } from '@/utils/report';
import BulletedList from '../ui/BulletedList';
import SectionHeading from '../ui/SectionHeading';
import { parseSwotContent } from '@/utils/report/sectionUtils';

interface SwotSectionProps {
  content: string;
  index: number;
}

const SwotSection: React.FC<SwotSectionProps> = ({ content, index }) => {
  // Parse the SWOT section using the improved parser
  const swotData = parseSwotContent(content);

  return (
    <div className="my-8 bg-gray-800/30 rounded-lg p-4">
      {/* Main SWOT heading */}
      {swotData.mainHeading && (
        <SectionHeading 
          text={swotData.mainHeading} 
          level="h2" 
          className="mb-4"
        />
      )}
      
      {/* Strengths section */}
      {swotData.strengths.length > 0 && (
        <div className="mb-4">
          <SectionHeading text="Strengths" level="h3" className="mb-2 text-green-400" />
          <ul className="ml-4 list-none space-y-2">
            {swotData.strengths.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-green-400 mr-2.5 inline-block mt-1 flex-shrink-0">•</span>
                <span className="text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Weaknesses section */}
      {swotData.weaknesses.length > 0 && (
        <div className="mb-4">
          <SectionHeading text="Weaknesses" level="h3" className="mb-2 text-red-400" />
          <ul className="ml-4 list-none space-y-2">
            {swotData.weaknesses.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-red-400 mr-2.5 inline-block mt-1 flex-shrink-0">•</span>
                <span className="text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Opportunities section */}
      {swotData.opportunities.length > 0 && (
        <div className="mb-4">
          <SectionHeading text="Opportunities" level="h3" className="mb-2 text-blue-400" />
          <ul className="ml-4 list-none space-y-2">
            {swotData.opportunities.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-blue-400 mr-2.5 inline-block mt-1 flex-shrink-0">•</span>
                <span className="text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Threats section */}
      {swotData.threats.length > 0 && (
        <div className="mb-4">
          <SectionHeading text="Threats" level="h3" className="mb-2 text-yellow-400" />
          <ul className="ml-4 list-none space-y-2">
            {swotData.threats.map((item, idx) => (
              <li key={idx} className="flex items-start">
                <span className="text-yellow-400 mr-2.5 inline-block mt-1 flex-shrink-0">•</span>
                <span className="text-gray-300" dangerouslySetInnerHTML={{ __html: formatText(item) }} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Removed the fallback message that was here previously */}
    </div>
  );
};

export default memo(SwotSection);
